import { Box } from "@mui/material";
import { DotLegalLink } from "@dotlegal/dotlegal-ui-components";
import { EntityType } from "@common/Common.types";
import OverflowTooltip from "@common/components/OverflowTooltip";
import { useUrlProvider } from "../../useUrlProvider";
import IndicatorRiskChip from "../IndicatorRiskChip";
import { RiskAreaHighestRisk } from "./RiskAreaRisks.types";

export interface IRiskAreaRiskLines {
    riskAreas: Array<RiskAreaHighestRisk>;
    entityType: EntityType;
    entityId: string;
    rowGap?: number;
}

// "area name: level chip" lines, shared by the RISK tile, its multi-area dialog and the overview table cells.
// One grid for all lines so every chip starts at the same x. The name track may shrink to zero while the chip
// track never does, so in a narrow table cell a long area name is cut off (full name in a tooltip) instead of
// wrapping and pushing its chip out of line. Both the name and the chip link to the area's risk assessments overview.
function RiskAreaRiskLines({ riskAreas, entityType, entityId, rowGap = 0.5 }: IRiskAreaRiskLines) {
    const { getRiskAssessmentsOverviewUrl } = useUrlProvider();

    return (
        <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, max-content) max-content", alignItems: "center", columnGap: 1, rowGap: rowGap }}>
            {riskAreas.map((riskArea) => {
                const linkHref = getRiskAssessmentsOverviewUrl(riskArea.riskAreaId, entityType, entityId);
                return (
                    <Box key={riskArea.riskAreaId} sx={{ display: "contents" }}>
                        <DotLegalLink to={linkHref} linkColor="primary-dark" size="small" stopPropagation>
                            <Box
                                sx={(theme) => ({
                                    display: "flex",
                                    minWidth: 0,
                                    fontSize: theme.typography.pxToRem(12),
                                    fontWeight: 400,
                                    lineHeight: theme.typography.body1.lineHeight,
                                })}
                            >
                                <OverflowTooltip text={riskArea.riskAreaName} />:
                            </Box>
                        </DotLegalLink>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <IndicatorRiskChip
                                level={riskArea.risk}
                                label={riskArea.riskLabel}
                                riskLevelIndicators={riskArea.riskLevelIndicators}
                                linkTo={linkHref}
                            />
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

export default RiskAreaRiskLines;
